// @systeme: auth-identite
// ============================================================================
// CECODEV Platform - Signup Page (v2 - 3 types INTER + 2FA)
// ============================================================================

import { ArrowLeft, GraduationCap, User } from "lucide-react";
import { useState, useRef, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Stack,
  CircularProgress,
  Alert,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
  IconButton,
  Stepper,
  Step as MuiStep,
  StepLabel,
} from "@mui/material";
import { api, donneesErreurApi, messageApi, champApi } from "../lib/api";
import { toast } from "sonner";
import { useAuth, getPostLoginDestination } from "../contexts/AuthContext";

// PILOTE retiré de l'inscription publique (FX 2026-08-18) : les organisations
// sont créées par Cecodev (guichet Intra), qui paramètre tout pour réduire la
// friction. Le flux serveur « demande de création d'org » a été purgé en
// vague 2 (N162, 2026-09-14) et la valeur PILOTE sortie de l'enum (0287).
type InscriptionType = "PARTICIPANT_INTER" | "ALUMNI_CECODEV";
type Step = "TYPE" | "INFO" | "VERIFY" | "PENDING" | "DONE";

export default function SignupPage() {
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [searchParams] = useSearchParams();
  const returnTo = searchParams.get("returnTo");
  // Quand on vient du catalogue / d'un parcours d'inscription (fiche formation
  // ou funnel d'inscription à un groupe), on est forcément PARTICIPANT : on
  // saute l'étape « Type » (déroutante pour le candidat) et on pré-sélectionne.
  const isParticipantFlow =
    !!returnTo &&
    (returnTo.startsWith("/plateforme/formation/") ||
      returnTo.startsWith("/plateforme/inscription/"));
  // Porte alumni de la page d'accueil (?type=alumni) : type présélectionné,
  // étape « Type » sautée — symétrique du flux participant ci-dessus.
  const isAlumniFlow = searchParams.get("type") === "alumni";
  const typeInitial: InscriptionType | null = isParticipantFlow
    ? "PARTICIPANT_INTER"
    : isAlumniFlow
      ? "ALUMNI_CECODEV"
      : null;
  const [step, setStep] = useState<Step>(typeInitial ? "INFO" : "TYPE");
  const [typeInscription, setTypeInscription] = useState<InscriptionType | null>(typeInitial);

  // Infos personnelles
  const [prenom, setPrenom] = useState("");
  const [nom, setNom] = useState("");
  const [email, setEmail] = useState("");
  const [telephone, setTelephone] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  // 2FA
  const [userId, setUserId] = useState<string | null>(null);
  const [emailMasked, setEmailMasked] = useState("");
  const [code, setCode] = useState("");
  const [resendCooldown, setResendCooldown] = useState(0);

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  // Action suggérée par le serveur quand l'inscription est refusée
  // (email connu, compte désactivé/en attente…) — cf. MODES_INSCRIPTION.md.
  const [errorAction, setErrorAction] = useState<{
    code?: string;
    suggestedAction?: string;
    canResetPassword?: boolean;
  } | null>(null);
  const [reactivationSent, setReactivationSent] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [codeExpiresIn, setCodeExpiresIn] = useState(0);
  const codeInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (step === "VERIFY") {
      setTimeout(() => codeInputRef.current?.focus(), 100);
      setCodeExpiresIn(600); // 10 minutes
    }
  }, [step]);

  useEffect(() => {
    if (resendCooldown > 0) {
      const t = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(t);
    }
  }, [resendCooldown]);

  useEffect(() => {
    if (step !== "VERIFY" || codeExpiresIn <= 0) return;
    const t = setTimeout(() => setCodeExpiresIn(codeExpiresIn - 1), 1000);
    return () => clearTimeout(t);
  }, [codeExpiresIn, step]);

  // Validation temps réel
  const validateField = (field: string, value: string) => {
    const errs = { ...fieldErrors };
    switch (field) {
      case "email":
        if (value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) errs.email = "Format email invalide";
        else delete errs.email;
        break;
      case "password":
        if (value && value.length < 8) errs.password = "Au moins 8 caractères";
        else delete errs.password;
        if (confirmPassword && value !== confirmPassword) errs.confirmPassword = "Les mots de passe ne correspondent pas";
        else delete errs.confirmPassword;
        break;
      case "confirmPassword":
        if (value && value !== password) errs.confirmPassword = "Les mots de passe ne correspondent pas";
        else delete errs.confirmPassword;
        break;
    }
    setFieldErrors(errs);
  };

  function handleSelectType(type: InscriptionType) {
    setTypeInscription(type);
    setStep("INFO");
    setError("");
  }

  function handleInfoNext(e: React.FormEvent) {
    e.preventDefault();
    setError("");

    if (password.length < 8) { setError("Le mot de passe doit contenir au moins 8 caractères"); return; }
    if (password !== confirmPassword) { setError("Les mots de passe ne correspondent pas"); return; }
    // Parcours participant (inscription) : téléphone mobile obligatoire —
    // l'animateur appelle pour confirmer l'inscription, et ça évite de le
    // redemander dans le funnel.
    if (isParticipantFlow && !telephone.trim()) {
      setError("Votre numéro de téléphone mobile est obligatoire : l'animateur vous appellera pour confirmer votre inscription.");
      return;
    }

    handleSubmit();
  }

  async function handleRequestReactivation() {
    try {
      await api.post("/auth/request-reactivation", { email: email.toLowerCase() });
      setReactivationSent(true);
      toast.success("Demande de réactivation envoyée");
    } catch {
      // Réponse anti-énumération côté serveur : on confirme quand même.
      setReactivationSent(true);
    }
  }

  async function handleSubmit() {
    setIsLoading(true);
    setError("");
    setErrorAction(null);
    setReactivationSent(false);

    try {
      const response = await api.post("/auth/register", {
        email: email.toLowerCase(),
        password,
        prenom,
        nom,
        telephone: telephone || undefined,
        typeInscription,
      });

      setUserId(response.data.userId);
      setEmailMasked(response.data.emailMasked);
      setStep("VERIFY");
      setResendCooldown(60);
      toast.success("Code de vérification envoyé !");
    } catch (err) {
      const msg = messageApi(err, "Erreur lors de l'inscription");
      setError(msg);
      const suggestedAction = champApi(err, "suggestedAction");
      const code = champApi(err, "code");
      if (suggestedAction || code) {
        setErrorAction({
          code: code ?? undefined,
          suggestedAction: suggestedAction ?? undefined,
          canResetPassword: donneesErreurApi(err)?.canResetPassword === true,
        });
      }
      toast.error(msg);
    } finally {
      setIsLoading(false);
    }
  }

  async function handleVerify(e: React.FormEvent) {
    e.preventDefault();
    if (code.length !== 6) return;
    setIsLoading(true);
    setError("");

    try {
      const response = await api.post("/auth/verify-2fa", { userId, code, trustDevice: true });

      if (response.data.step === "PENDING_VALIDATION") {
        setStep("PENDING");
      } else if (response.data.step === "COMPLETE") {
        localStorage.setItem("token", response.data.token);
        // Rafraîchir le contexte d'auth AVANT de naviguer : sinon le funnel
        // (qui gate sur `user`) ne voit pas encore la session et renvoie vers
        // la connexion. On enchaîne ensuite directement sur le returnTo.
        await refreshUser();
        setStep("DONE");
        toast.success("Compte créé !");
        // Même règle que la connexion (moteur unique) : un non-pilote atterrit
        // sur SON espace, pas sur /projets qui lui répondrait « accès non
        // autorisé » — premier écran d'un nouveau compte, il vaut mieux.
        const destination =
          returnTo && returnTo.startsWith("/") && !returnTo.startsWith("//")
            ? returnTo
            : getPostLoginDestination(response.data.user);
        setTimeout(() => navigate(destination), 1200);
      }
    } catch (err) {
      setError(messageApi(err, "Code invalide"));
      setCode("");
    } finally {
      setIsLoading(false);
    }
  }

  async function handleResend() {
    if (!userId) return;
    try {
      await api.post("/auth/resend-code", { userId });
      toast.success("Nouveau code envoyé");
      setResendCooldown(60);
    } catch { toast.error("Erreur lors du renvoi"); }
  }

  const typeLabels: Record<InscriptionType, { icon: React.ReactNode; label: string; desc: string }> = {
    PARTICIPANT_INTER: {
      icon: <User size={32} />,
      label: "Participant",
      desc: "Rejoindre des groupes de codéveloppement",
    },
    ALUMNI_CECODEV: {
      icon: <GraduationCap size={32} />,
      label: "Alumni du Cecodev",
      desc: "J'ai suivi le Module Vert ou Rouge — e-learning, vidéos, groupes d'entraînement",
    },
  };

  return (
    <Box className="auth-background">
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "100vh", px: 2, py: 4 }}>
        <Card sx={{ width: "100%", maxWidth: 600, boxShadow: "0 20px 60px rgba(0, 0, 0, 0.15)" }} className="fade-in">
          <CardContent sx={{ p: 5 }}>
            {/* Header */}
            <Box sx={{ textAlign: "center", mb: 4 }}>
              <Box
                component="img"
                src="/logo-pairsform-cecodev-v2.png"
                alt="Pairsform — l'intelligence collective facilitée, par Cecodev"
                sx={{ width: "100%", maxWidth: 320, height: "auto", mx: "auto", display: "block", mb: 1.5 }}
              />
              <Typography variant="body2" color="text.secondary">Créer votre compte</Typography>
            </Box>

            {/* Stepper visuel */}
            {!["PENDING", "DONE"].includes(step) && (
              <Stepper
                activeStep={
                  isParticipantFlow
                    ? (step === "VERIFY" ? 1 : 0)
                    : step === "TYPE" ? 0 : step === "INFO" ? 1 : step === "VERIFY" ? 2 : 0
                }
                alternativeLabel
                sx={{ mb: 3, "& .MuiStepLabel-label": { fontSize: "0.75rem" } }}
              >
                {!isParticipantFlow && <MuiStep><StepLabel>Type</StepLabel></MuiStep>}
                <MuiStep><StepLabel>Informations</StepLabel></MuiStep>
                <MuiStep><StepLabel>Vérification</StepLabel></MuiStep>
              </Stepper>
            )}

            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
                {errorAction && (() => {
                  const loginHref = returnTo
                    ? `/login?returnTo=${encodeURIComponent(returnTo)}`
                    : "/login";
                  const action = errorAction.suggestedAction;
                  const isReactivation = action === "REQUEST_REACTIVATION";
                  const showLogin =
                    action === "LOGIN" ||
                    action === "RESEND_VERIFICATION" ||
                    action === "WAIT_VALIDATION";
                  const showReset =
                    action === "RESET_PASSWORD" ||
                    (errorAction.canResetPassword && !isReactivation);
                  if (!showLogin && !showReset && !isReactivation) return null;
                  return (
                    <Stack direction="row" spacing={2} sx={{ mt: 1.5, alignItems: "center" }}>
                      {showLogin && (
                        <Link to={loginHref} style={{ color: "#4BB7A5", fontWeight: 600, textDecoration: "none" }}>
                          Se connecter
                        </Link>
                      )}
                      {showReset && (
                        <Link to="/forgot-password" style={{ color: "#4BB7A5", fontWeight: 600, textDecoration: "none" }}>
                          Mot de passe oublié ?
                        </Link>
                      )}
                      {isReactivation && (
                        reactivationSent ? (
                          <Typography variant="body2" sx={{ color: "text.secondary" }}>
                            Demande envoyée — vérifiez vos emails ou patientez la validation.
                          </Typography>
                        ) : (
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={handleRequestReactivation}
                            sx={{ textTransform: "none" }}
                          >
                            Demander la réactivation
                          </Button>
                        )
                      )}
                    </Stack>
                  );
                })()}
              </Alert>
            )}

            {/* ── Étape 1 : Choix du type ── */}
            {step === "TYPE" && (
              <Stack spacing={2}>
                <Typography variant="h6" sx={{ fontWeight: 600, textAlign: "center", mb: 1 }}>
                  Je m'inscris en tant que...
                </Typography>

                {(Object.entries(typeLabels) as [InscriptionType, typeof typeLabels[InscriptionType]][]).map(([type, info]) => (
                  <Button
                    key={type}
                    variant="outlined"
                    onClick={() => handleSelectType(type)}
                    sx={{
                      p: 2,
                      justifyContent: "flex-start",
                      textAlign: "left",
                      textTransform: "none",
                      borderColor: "divider",
                      "&:hover": { borderColor: "#4BB7A5", bgcolor: "rgba(75, 183, 165, 0.04)" },
                    }}
                  >
                    <Box sx={{ mr: 2, color: "#4BB7A5" }}>{info.icon}</Box>
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{info.label}</Typography>
                      <Typography variant="body2" color="text.secondary">{info.desc}</Typography>
                    </Box>
                  </Button>
                ))}

                <Box sx={{ mt: 2, textAlign: "center" }}>
                  <Typography variant="body2" color="text.secondary">
                    Déjà un compte ?{" "}
                    <Link to={returnTo ? `/login?returnTo=${encodeURIComponent(returnTo)}` : "/login"} style={{ color: "#4BB7A5", textDecoration: "none", fontWeight: 600 }}>Se connecter</Link>
                  </Typography>
                </Box>
              </Stack>
            )}

            {/* ── Étape 2 : Infos personnelles ── */}
            {step === "INFO" && (
              <form onSubmit={handleInfoNext}>
                <Stack spacing={2.5}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    {!isParticipantFlow && (
                      <IconButton onClick={() => setStep("TYPE")} size="small"><ArrowLeft size={18} /></IconButton>
                    )}
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {typeLabels[typeInscription!].label} — Vos informations
                    </Typography>
                  </Box>

                  <Box sx={{ display: "flex", gap: 2, flexDirection: { xs: "column", sm: "row" } }}>
                    <TextField label="Prénom" value={prenom} onChange={(e) => setPrenom(e.target.value)} required fullWidth autoComplete="given-name" />
                    <TextField label="Nom" value={nom} onChange={(e) => setNom(e.target.value)} required fullWidth autoComplete="family-name" />
                  </Box>

                  <TextField label="Email" type="email" value={email} onChange={(e) => { setEmail(e.target.value); validateField("email", e.target.value); }} error={!!fieldErrors.email} helperText={fieldErrors.email} required fullWidth placeholder="votre@email.com" autoComplete="email" />
                  <TextField label={isParticipantFlow ? "Téléphone mobile *" : "Téléphone (optionnel)"} value={telephone} onChange={(e) => setTelephone(e.target.value)} required={isParticipantFlow} fullWidth autoComplete="tel" helperText={isParticipantFlow ? "L'animateur vous appellera pour confirmer votre inscription." : undefined} />

                  <Divider />

                  <TextField label="Mot de passe" type="password" value={password} onChange={(e) => { setPassword(e.target.value); validateField("password", e.target.value); }} error={!!fieldErrors.password} helperText={fieldErrors.password || "Au moins 8 caractères"} required fullWidth autoComplete="new-password" />
                  <TextField label="Confirmer" type="password" value={confirmPassword} onChange={(e) => { setConfirmPassword(e.target.value); validateField("confirmPassword", e.target.value); }} error={!!fieldErrors.confirmPassword} helperText={fieldErrors.confirmPassword} required fullWidth autoComplete="new-password" />

                  <Button type="submit" variant="contained" size="large" fullWidth disabled={isLoading}>
                    {isLoading ? <CircularProgress size={24} color="inherit" /> : "Créer mon compte"}
                  </Button>
                </Stack>
              </form>
            )}

            {/* ── Étape 3 : Code de vérification ── */}
            {step === "VERIFY" && (
              <form onSubmit={handleVerify}>
                <Stack spacing={3}>
                  <Typography variant="h6" sx={{ fontWeight: 600, textAlign: "center" }}>Vérifiez votre email</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center" }}>
                    Un code à 6 chiffres a été envoyé à<br /><strong>{emailMasked}</strong>
                  </Typography>

                  {codeExpiresIn > 0 && (
                    <Typography variant="caption" sx={{ textAlign: "center", display: "block", color: codeExpiresIn < 120 ? "error.main" : "text.secondary" }}>
                      Code valide pendant {Math.floor(codeExpiresIn / 60)}:{(codeExpiresIn % 60).toString().padStart(2, "0")}
                    </Typography>
                  )}

                  <TextField
                    inputRef={codeInputRef}
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    onPaste={(e) => { const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6); if (pasted) { setCode(pasted); e.preventDefault(); } }}
                    required
                    fullWidth
                    placeholder="000000"
                    inputProps={{ maxLength: 6, style: { textAlign: "center", fontSize: "1.5rem", letterSpacing: "0.5em" }, inputMode: "numeric" }}
                    autoComplete="one-time-code"
                  />

                  <Button type="submit" variant="contained" size="large" fullWidth disabled={isLoading || code.length !== 6}>
                    {isLoading ? <CircularProgress size={24} color="inherit" /> : "Valider"}
                  </Button>

                  <Box sx={{ textAlign: "center" }}>
                    <Button variant="text" size="small" onClick={handleResend} disabled={resendCooldown > 0} sx={{ color: "#4BB7A5" }}>
                      {resendCooldown > 0 ? `Renvoyer (${resendCooldown}s)` : "Renvoyer le code"}
                    </Button>
                  </Box>
                </Stack>
              </form>
            )}

            {/* ── En attente de validation ── */}
            {step === "PENDING" && (
              <Stack spacing={3} sx={{ textAlign: "center" }}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: "#f59e0b" }}>En attente de validation</Typography>
                <Typography variant="body2" color="text.secondary">
                  Votre email a été vérifié. L'équipe Cecodev va examiner votre demande.
                  Vous recevrez un email dès que votre compte sera activé.
                </Typography>
                <Button component={Link} to="/login" variant="contained">Retour à la connexion</Button>
              </Stack>
            )}

            {/* ── Compte créé ── */}
            {step === "DONE" && (
              <Stack spacing={3} sx={{ textAlign: "center" }}>
                <Typography variant="h5" sx={{ fontWeight: 700, color: "#4BB7A5" }}>Bienvenue !</Typography>
                <Typography variant="body2" color="text.secondary">Votre compte a été créé. Redirection en cours...</Typography>
                <CircularProgress sx={{ mx: "auto" }} />
              </Stack>
            )}
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
}
